import React, { useContext, useEffect, useMemo, useState } from "react";
import "./my-coupons.scss";
import NavigationBar from "../../../../components/NavigationBar/navigationbar";
import CouponCard from "../../../../components/CouponCard/coupon-card";
import httpclient from "../../../../service/httpclient";
import { CouponStatus } from "./coupon-enums";
import { Empty, Tooltip, Typography } from "antd";
import { UserContext } from "../../../../App";
import moment from "moment";
import { useLocation } from "react-router-dom";
import {
  GiftOutlined,
  RightOutlined,
  TrophyOutlined,
  WalletOutlined,
} from "@ant-design/icons";

const { Paragraph } = Typography;
const LOTTERY_RECORDS_TAB = "lottery_records";
const COUPON_TABS = [
  CouponStatus.AVAILABLE,
  CouponStatus.USED,
  CouponStatus.UNUSED,
  LOTTERY_RECORDS_TAB,
];

function getInitialTab(search) {
  const tab = new URLSearchParams(search).get("tab");
  return COUPON_TABS.includes(tab) ? tab : CouponStatus.AVAILABLE;
}

function getCouponCreatedTime(coupon) {
  if (!coupon?.created_at) return 0;
  const time = moment.utc(coupon.created_at);
  return time.isValid() ? time.valueOf() : 0;
}

function sortCouponsByCreatedDesc(coupons) {
  return [...coupons].sort(
    (couponA, couponB) =>
      getCouponCreatedTime(couponB) - getCouponCreatedTime(couponA)
  );
}

function getLotteryRecordMeta(type) {
  if (type === "points") {
    return {
      tag: "权益积分",
      icon: <TrophyOutlined />,
      className: "points",
      actionText: "查看权益",
      actionLink: "/user/center/my-rights",
    };
  }
  if (type === "live_coupon") {
    return {
      tag: "直播抵扣券",
      icon: <WalletOutlined />,
      className: "coupon",
      actionText: "查看优惠券",
      actionLink: "/user/center/my-coupons",
    };
  }
  if (type === "column_coupon" || type === "coupon") {
    return {
      tag: "专栏优惠券",
      icon: <GiftOutlined />,
      className: "coupon",
      actionText: "查看优惠券",
      actionLink: "/user/center/my-coupons",
    };
  }
  return {
    tag: "抽奖奖励",
    icon: <GiftOutlined />,
    className: "default",
    actionText: "查看",
    actionLink: "/user/center/my-coupons",
  };
}

function MyCoupons() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() =>
    getInitialTab(location.search)
  );
  const [couponsData, setCouponsData] = useState(null);
  const [lotteryRecords, setLotteryRecords] = useState(null);
  const { userInfo } = useContext(UserContext);
  useEffect(() => {
    httpclient.getCoupons().then((res) => {
      if (res) {
        setCouponsData(res);
      }
    });
    httpclient.getLotteryRecords().then((res) => {
      if (res) {
        setLotteryRecords(res.records || []);
      }
    });
  }, []);
  useEffect(() => {
    setActiveTab(getInitialTab(location.search));
  }, [location.search]);
  const handleChangeTab = (key) => {
    setActiveTab(key);
  };
  const lotteryRecordsContent = useMemo(() => {
    if (!lotteryRecords) return null;
    if (!lotteryRecords.length) {
      return (
        <div className="lottery-record-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="暂无抽奖奖励记录"
          />
        </div>
      );
    }

    return (
      <div className="lottery-record-list">
        {lotteryRecords.map((record) => {
          const prize = record.prize || {};
          const meta = getLotteryRecordMeta(prize.type);
          return (
            <div
              className={`lottery-record-card lottery-record-card-${meta.className}`}
              key={record.id}
            >
              <div className="lottery-record-icon">{meta.icon}</div>
              <div className="lottery-record-main">
                <div className="lottery-record-head">
                  <span className="lottery-record-name">
                    {prize.name || prize.label}
                  </span>
                  <span className="lottery-record-tag">{meta.tag}</span>
                </div>
                <div className="lottery-record-description">
                  {prize.description || "奖品已自动发放至你的账户"}
                </div>
                <div className="lottery-record-time">
                  抽奖时间：
                  {record.drawn_at
                    ? moment.utc(record.drawn_at).local().format("YYYY.MM.DD HH:mm")
                    : "--"}
                </div>
              </div>
              <a className="lottery-record-action" href={meta.actionLink}>
                {meta.actionText}
                <RightOutlined />
              </a>
            </div>
          );
        })}
      </div>
    );
  }, [lotteryRecords]);
  const content = useMemo(() => {
    if (activeTab === LOTTERY_RECORDS_TAB) {
      return lotteryRecordsContent;
    }
    return (
      <CouponCard
        cardData={sortCouponsByCreatedDesc(
          couponsData?.filter((item) => {
            if (
              item.status !== CouponStatus.USED &&
              item.status !== CouponStatus.EXPIRED &&
              item.status !== CouponStatus.LOCKED
            ) {
              return item.status === activeTab;
            } else
              return (
                activeTab === CouponStatus.USED ||
                activeTab === CouponStatus.EXPIRED ||
                activeTab === CouponStatus.LOCKED
              );
          }) || []
        )}
      />
    );
  }, [activeTab, couponsData, lotteryRecordsContent]);
  const NavigationBarData = [
    {
      title: "可使用",
      key: CouponStatus.AVAILABLE,
      onClick: () => {
        handleChangeTab(CouponStatus.AVAILABLE);
      },
    },
    {
      title: "已过期/已使用",
      key: CouponStatus.USED,
      onClick: () => {
        handleChangeTab(CouponStatus.USED);
      },
    },
    {
      title: "待生效",
      key: CouponStatus.UNUSED,
      onClick: () => {
        handleChangeTab(CouponStatus.UNUSED);
      },
    },
    {
      title: "抽奖记录",
      key: LOTTERY_RECORDS_TAB,
      onClick: () => {
        handleChangeTab(LOTTERY_RECORDS_TAB);
      },
    },
  ];
  return (
    <div className="coupons-wrapper">
      <div className="coupons-title">
        <div>优惠券</div>
        <div className="recommend-code">
          <Tooltip title="分享引荐码给好友，新用户首次购买可享5折优惠，您也可获得50元抵扣券（适用于引荐人和新用户都购买成功情况）">
            <span>
              <img
                alt=""
                style={{ width: "22px", marginRight: "4px" }}
                src={
                  "https://cdn.datongbei.com/icdesign-17014217294306b8h88a3cb07.png?imageView2/0/format/jpg/q/90|imageslim"
                }
              />
            </span>
            优惠引荐码:{" "}
          </Tooltip>
          <span className="code">
            <Paragraph copyable>{userInfo?.note_token?.slice(0, 6)}</Paragraph>
          </span>
        </div>
      </div>
      {NavigationBar(NavigationBarData, activeTab)}
      {!!(
        couponsData ||
        (activeTab === LOTTERY_RECORDS_TAB && lotteryRecords)
      ) && <div className="coupons-content">{content}</div>}
    </div>
  );
}

export default MyCoupons;
